import React, { useState } from 'react';
import { Users, Heart, Book, Globe, ChevronDown, BookOpen, Mic2, Sunrise } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const HERO_IMAGE = 'https://tsoministries.com/wp-content/uploads/2025/09/B_H00766-scaled.jpeg';
const STORY_IMAGE = 'https://tsoministries.com/wp-content/uploads/2025/09/B_H00635-scaled.jpeg';

const MINISTRY_IMAGES = {
  'Healing Party': 'https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.01.51-PM.jpeg',
  'Bible Study with Titoluwa': 'https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-23-at-12.35.15-PM.jpeg',
  'Christocentric Prayers': 'https://tsoministries.com/wp-content/uploads/2025/07/morningPrayer.jpg',
  'Bible Study Masterclass': 'https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.00.09-PM.jpeg',
  'Mentorship with HS': 'https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-12.55.41-PM.jpeg',
  'Life in the Spirit (LITS)': 'https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-12.55.15-PM.jpeg',
};

const BOOK_GRADIENTS = [
  'from-amber-900 via-orange-800 to-amber-700',
  'from-stone-800 via-neutral-700 to-stone-600',
  'from-rose-900 via-red-800 to-rose-700',
];

const LEADER_COLORS = [
  'from-orange-600 to-amber-500',
  'from-amber-600 to-yellow-500',
  'from-red-700 to-orange-600',
  'from-stone-600 to-amber-700',
  'from-orange-700 to-red-600',
  'from-amber-700 to-orange-500',
  'from-yellow-700 to-amber-600',
  'from-orange-500 to-amber-400',
  'from-red-600 to-orange-500',
  'from-amber-500 to-yellow-400',
  'from-orange-600 to-red-500',
];

function getInitials(name) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('');
}

function FadeIn({
  children,
  delay = 0,
  className = '',
}) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function StatCard({
  stat,
  index,
}) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={`group relative overflow-hidden rounded-2xl border border-black/[0.08] bg-black/[0.04] p-1 py-5 md:p-8 md:py-8 text-center transition-all duration-700 ease-out hover:border-orange-500/40 hover:bg-black/[0.08] ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <stat.icon className="mx-auto mb-4 h-7 w-7 text-orange-400 transition-transform duration-300 group-hover:scale-110" />
      <div className="mb-1 text-sm md:text-xl lg:text-4xl font-bold tracking-tight text-black">{stat.value}</div>
      <div className="text-[8px] md:text-sm uppercase tracking-widest text-gray-500">{stat.label}</div>
    </div>
  );
}

export default function About() {
  const [heroLoaded, setHeroLoaded] = useState(false);

  const stats = [
    { label: 'Lives Touched', value: '10,000+', icon: Users },
    { label: 'Years of Ministry', value: '10+', icon: Heart },
    { label: 'Bible Studies', value: '100+', icon: Book },
    { label: 'Global Reach', value: '5+ Countries', icon: Globe },
  ];

  const values = [
    {
      icon: Sunrise,
      title: 'Our Vision',
      description:
        'To transform lives through the power of Christ, creating a global community of believers who live and walk in the Spirit.',
    },
    {
      icon: Globe,
      title: 'Our Mission',
      description:
        "To equip believers with biblical truth, foster spiritual growth, and demonstrate God\'s love through practical ministry.",
    },
    {
      icon: Heart,
      title: 'Our Values',
      description:
        'We are committed to biblical truth, spiritual excellence, compassionate service, and authentic community.',
    },
    {
      icon: Mic2,
      title: 'Our Expressions',
      description:
        "We create spaces for growth, and transformation. Wherever you are in your journey, there’s something here for you.",
    },
  ];

  const ministries = [
    {
      title: 'Healing Party',
      description:
        'A yearly program hosted in different parts of the world, focused on healing and restoration of identity through the Word, prayer, and worship.',
    },
    {
      title: 'Bible Study with Titoluwa',
      description:
        'A weekly Bible study community that meets every Monday and Thursday at 7PM WAT across YouTube, TikTok, and Instagram, studying books of the Bible together chapter by chapter.',
    },
    {
      title: 'Christocentric Prayers',
      description:
        'A daily 30-minute Christ-centered prayer experience at 10PM WAT, designed to build consistency, deepen your devotion, and strengthen your walk with God.',
    },
    {
      title: 'Bible Study Masterclass',
      description:
        'A yearly program designed to deepen your understanding of Scripture and equip you with practical tools for studying the Bible effectively.',
    },
    {
      title: 'Mentorship with HS',
      description:
        "A transformative 7-week journey of immersing in God's Word, building spiritual discipline, and growing in intimacy with the Holy Spirit.",
    },
    {
      title: 'Life in the Spirit (LITS)',
      description:
        'A focused prayer and Word-based program designed to build spiritual consistency, deepen your prayer life, and strengthen your walk in the Spirit.',
    },
  ];

  const books = [
    {
      title: 'FOCUS',
      description:
        'A Christ-centered book designed to help you keep your eyes on Jesus in a world full of distractions. Rooted in Scripture, it calls you back to truth, helping you fix your gaze on what is eternal.',
      gradient: BOOK_GRADIENTS[0],
      image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.02.00-PM.jpeg"
    },
    {
      title: 'Christocentric Prayer Booklet',
      description:
        'A 30-day devotional guide designed to shift your prayer life from hoping something will happen to knowing something has already been done in Christ. It is rooted in Scripture and focused on declarations that renew your mind and anchor you in your identity and inheritance in Him.',
      gradient: BOOK_GRADIENTS[1],
      image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.02.57-PM.jpeg",
    },
    {
      title: 'Pop My Cherry Now',
      description:
        'A deeply personal story born out of a season of pain, confusion, and unanswered questions around vaginismus and faith. It is a testimony of how a hidden struggle became a journey of healing, restoration, and rediscovery of identity in God.',
      gradient: BOOK_GRADIENTS[2],
      image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.02.04-PM.jpeg"
    },
  ];

  const leaders = [
    { name: 'TitOluwa Olumideko', role: 'Lead Pastor, TSO Ministries', image: "https://healing-pavilion.com/wp-content/uploads/2026/02/healing-pavilion4.jpg" },
    { name: 'Iyinsioluwa Ojo', role: 'Technical Director', image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-3.21.32-PM.jpeg" },
    { name: 'Joanneta James', role: 'Executive Director', image: "https://tsoministries.com/wp-content/uploads/2026/04/MJJ.jpg" },
    { name: 'Adeagbo Ayotioluwafunmi', role: 'Assistant Executive Director', image: "https://tsoministries.com/wp-content/uploads/2026/04/Ayotioluwafunmi-.JPG.jpeg" },
    { name: 'Grace Olawumi', role: 'Team Lead: Content Department', image: "https://tsoministries.com/wp-content/uploads/2026/04/YSA00382-1.jpg-scaled.jpeg" },
    { name: 'Edidiong Isaiah', role: 'Team Lead: Prayer Department', image: "https://tsoministries.com/wp-content/uploads/2026/04/Edidiong_Isaiah.jpeg" },
    { name: 'Oladunni Treasure', role: 'Team Lead: BST Community', image: "https://tsoministries.com/wp-content/uploads/2026/04/IMG_3745.JPG-scaled.jpeg" },
    { name: 'Favour Atuchukwu', role: 'Team Lead: Videography Department', image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.10.26-PM.jpeg" },
    { name: 'Ogunleye Naomi', role: 'Team Lead: Scribe Department', image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.12.12-PM.jpeg" },
    { name: 'Deborah Bamidele', role: 'Team Lead: Community Manager', image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.13.12-PM.jpeg" },
    { name: 'Carter Yvonne', role: 'Team Lead: Follow-Up Department', image: "https://tsoministries.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-21-at-1.15.28-PM.jpeg" },
  ];

  return (
    <div className="min-h-screen bg-[#ffffff] text-black">
      {/* ── Hero ── */}
      <section className="relative flex h-screen items-end overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="TSO Ministries worship"
          onLoad={() => setHeroLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${heroLoaded ? 'opacity-100' : 'opacity-0'
            }`}
        />
        {/* layered gradients */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/50 to-transparent" />

        <div className="relative z-10 w-full pb-20">
          <div className="mx-auto max-w-6xl px-6">
            <div
              className="max-w-2xl opacity-1"
            // style={{ animation: 'heroFadeIn 0.9s ease-out 0.3s forwards' }}
            >
              <span className="mb-5 inline-block rounded-full bg-orange-500/10 px-4 py-1.5 text-xs uppercase tracking-widest text-orange-400">
                TSO Ministries
              </span>
              <h1 className="mb-5 text-white text-5xl font-bold leading-tight tracking-tight md:text-7xl">
                Transforming Lives
                <br />
                <span className="text-orange-400">Through Christ</span>
              </h1>
              <p className="text-lg leading-relaxed text-gray-300 md:text-xl">
                A global ministry equipping believers with truth, fostering spiritual growth, and
                demonstrating God's love in every nation.
              </p>
            </div>
          </div>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-black/30">
          <ChevronDown className="h-6 w-6" />
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="mx-auto max-w-6xl px-2 py-8 md:px-6 md:py-20">
        <div className="grid grid-cols-4 gap-2 md:grid-cols-4 md:gap-6">
          {stats.map((stat, i) => (
            <StatCard key={i} stat={stat} index={i} />
          ))}
        </div>
      </section>

      {/* ── Divider ── */}
      <div className="mx-auto max-w-6xl px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
      </div>

      {/* ── Our Story ── */}
      <section className="mx-auto max-w-6xl px-6 py-10 md:py-24">
        <div className="grid items-center gap-16 md:grid-cols-2">
          <FadeIn>
            <span className="mb-4 block text-xs uppercase tracking-widest text-orange-500">
              Who We Are
            </span>
            <h2 className="mb-6 text-4xl font-bold leading-tight md:text-5xl">Our Story</h2>
            <div className="space-y-5 text-base leading-relaxed text-gray-400">
              <p>
                Founded over a decade ago, TSO Ministries began with a simple vision: to help
                people experience the transformative power of God's love. What started as small
                prayer meetings has grown into a global ministry reaching tens of thousands across
                five continents.
              </p>
              <p>
                Through the years, we've remained committed to our core mission of spreading the
                Gospel while adapting to meet the evolving needs of our community. Our approach
                combines deep biblical teaching with contemporary relevance, meeting people where
                they are and walking with them into who they are called to be.
              </p>
              <p>
                Every program, resource, and gathering is built around one conviction: that
                encounter with the living Word of God changes everything.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={150}>
            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-orange-500/20 to-amber-500/10 blur-xl" />
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={STORY_IMAGE}
                  alt="TSO Ministries community"
                  className="h-96 w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="bg-black/[0.02] py-10 md:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <FadeIn className="mb-14 text-center">
            <span className="mb-3 block text-xs uppercase tracking-widest text-orange-500">
              Foundation
            </span>
            <h2 className="text-4xl font-bold md:text-5xl">What We Stand For</h2>
          </FadeIn>
          <div className="grid gap-6 sm:grid-cols-2">
            {values.map((v, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="group relative h-full overflow-hidden rounded-2xl border border-black/[0.08] bg-black/[0.03] p-8 transition-all duration-300 hover:border-orange-500/30 hover:bg-black/5">
                  <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-orange-500 to-amber-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/10">
                    <v.icon className="h-5 w-5 text-orange-400" />
                  </div>
                  <h3 className="mb-3 text-xl font-semibold">{v.title}</h3>
                  <p className="leading-relaxed text-gray-400">{v.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Ministries ── */}
      <section className="mx-auto max-w-6xl px-6 py-10 md:py-24">
        <FadeIn className="mb-14 text-center">
          <span className="mb-3 block text-xs uppercase tracking-widest text-orange-500">
            What We Do
          </span>
          <h2 className="mb-4 text-4xl font-bold md:text-5xl">Ministry Expression</h2>
          <p className="mx-auto max-w-xl text-gray-400">
            Programs designed to meet you where you are and take you further in your walk with God.
          </p>
        </FadeIn>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ministries.map((ministry, i) => (
            <FadeIn key={i} delay={i * 60}>
              <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-black/[0.03] transition-all duration-300 hover:border-orange-500/30 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/5">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={MINISTRY_IMAGES[ministry.title]}
                    alt={ministry.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      (e.target).style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/20 px-3 py-1 text-xs font-medium text-orange-300">
                      <BookOpen className="h-3 w-3" />
                      Ministry
                    </span>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="mb-3 font-semibold text-black transition-colors duration-300 group-hover:text-orange-400">
                    {ministry.title}
                  </h3>
                  <p className="flex-1 text-sm leading-relaxed text-gray-500">{ministry.description}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── Publications ── */}
      <section className="bg-black/[0.02] py-10 md:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <FadeIn className="mb-14 text-center">
            <span className="mb-3 block text-xs uppercase tracking-widest text-orange-500">
              Resources
            </span>
            <h2 className="mb-4 text-4xl font-bold md:text-5xl">Our Publications</h2>
            <p className="mx-auto max-w-xl text-gray-400">
              Books and devotionals to anchor you in truth and equip you for daily life in the Spirit.
            </p>
          </FadeIn>
          <div className="grid gap-8 md:grid-cols-3">
            {books.map((book, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="group flex h-full flex-col">
                  {/* book cover */}
                  <div
                    className={`relative mb-6 flex h-80 items-end overflow-hidden rounded-2xl bg-gradient-to-br ${book.gradient} p-6 shadow-2xl transition-all duration-300 group-hover:-translate-y-2 group-hover:shadow-orange-500/10`}
                  >
                    <div className="absolute inset-0 opacity-20"
                      style={{
                        backgroundImage:
                          'repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 12px)',
                      }}
                    />
                    <img
                      src={book.image}
                      alt="TSO Ministries community"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                    <div className="relative">
                      <div className="mb-1 h-0.5 w-8 bg-orange-400/60" />
                      <h3 className="text-xl font-bold leading-tight tracking-tight text-white/90">
                        {book.title}
                      </h3>
                    </div>
                  </div>
                  <p className="flex-1 text-sm leading-relaxed text-gray-500">{book.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Leadership ── */}
      <section className="mx-auto max-w-6xl px-6py-10 md:py-24">
        <FadeIn className="mb-14 text-center">
          <span className="mb-3 block text-xs uppercase tracking-widest text-orange-500">
            The Team
          </span>
          <h2 className="mb-4 text-4xl font-bold md:text-5xl">Our Leadership</h2>
          <p className="mx-auto max-w-xl text-gray-400">
            Dedicated servants committed to building a community where every person encounters God.
          </p>
        </FadeIn>
        <div className="grid p-2 gap-2 md:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {leaders.map((leader, i) => (
            <FadeIn key={i} delay={i * 50}>
              <div className="group flex flex-col items-center rounded-2xl border border-black/[0.08] bg-black/[0.03] p-6 text-center transition-all duration-300 hover:border-orange-500/25 hover:bg-black/5 hover:-translate-y-1">
                {/* avatar */}
                <div className="relative w-48 h-48 mx-auto mb-4 rounded-full overflow-hidden">
                  <div
                  className={`absolute h-full w-full flex items-center justify-center rounded-full bg-gradient-to-br ${LEADER_COLORS[i % LEADER_COLORS.length]} text-lg font-bold text-black shadow-lg`}
                >
                  {getInitials(leader.name)}
                </div>
                  <img
                    src={leader.image}
                    alt={`Leader ${leader.name}`}
                    className="relative w-full h-full object-cover"
                  />
                </div>
                <h3 className="mb-1 text-sm font-semibold leading-snug text-black transition-colors duration-300 group-hover:text-orange-400">
                  {leader.name}
                </h3>
                <p className="text-xs leading-relaxed text-gray-500">{leader.role}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>
    </div>
  );
}
